import Images from "../utils/Images";
import { FAQ_ITEMS } from "./faqs";

export const MARKET_PORTAL = "https://consumer.simmarkets.info/";

export const MARKET_NAV = [
  { id: "intro", label: "Introduction" },
  { id: "why-simmarkets", label: "Why it matters" },
  { id: "vision", label: "Vision" },
  { id: "offers", label: "Marketplace" },
  { id: "engines", label: "Capabilities" },
  { id: "journey", label: "How it works" },
  { id: "platform", label: "Platform" },
  { id: "videos", label: "Videos" },
  { id: "faqs", label: "FAQs" },
  { id: "demo", label: "Get started" },
];

export const MARKET_PATH = [
  { n: "01", title: "Learn", line: "Education before a decision" },
  { n: "02", title: "Compare", line: "Transparent side-by-side facts" },
  { n: "03", title: "Decide", line: "AI-guided, confident choice" },
  { n: "04", title: "Connect", line: "Book with a verified provider" },
];

export const CONSUMER_CHALLENGES = [
  "Information overload",
  "Limited financial literacy",
  "Difficulty comparing products",
  "Lack of trusted financial advice",
  "Choosing unsuitable financial products",
  "Limited access to verified providers",
];

export const INSTITUTION_CHALLENGES = [
  "Rising customer acquisition costs",
  "Poor lead quality",
  "Low conversion rates",
  "Fragmented customer engagement",
  "High marketing expenditure",
  "Limited customer insights",
];

export const VISION_PILLARS = [
  {
    n: "01",
    title: "Education",
    copy: "Empower consumers to understand products before they commit.",
    icon: "book",
  },
  {
    n: "02",
    title: "Transparency",
    copy: "Make comparison honest, clear and free of marketing noise.",
    icon: "eye",
  },
  {
    n: "03",
    title: "Intelligence",
    copy: "Guide every choice with AI-powered financial decision-making.",
    icon: "bot",
  },
  {
    n: "04",
    title: "Acquisition",
    copy: "Help institutions grow through qualified, intent-driven customers.",
    icon: "target",
  },
];

export const OBJECTIVES = [
  {
    id: "consumers",
    index: "01",
    title: "For consumers",
    lede: "Make complex financial choices feel clear, trusted and personal.",
    items: [
      "Improve financial literacy.",
      "Simplify complex financial decisions.",
      "Enable transparent product comparisons.",
      "Provide trusted educational resources.",
      "Deliver personalized AI recommendations.",
      "Connect consumers with verified providers.",
      "Improve access to financial services.",
    ],
  },
  {
    id: "institutions",
    index: "02",
    title: "For financial institutions",
    lede: "Acquire customers who already understand what they need.",
    items: [
      "Generate qualified customer leads.",
      "Reduce customer acquisition costs.",
      "Increase conversion rates.",
      "Improve customer engagement.",
      "Strengthen brand visibility.",
      "Enhance customer nurturing.",
      "Improve marketing ROI.",
    ],
  },
  {
    id: "ecosystem",
    index: "03",
    title: "For the financial ecosystem",
    lede: "Raise the standard of how Africa buys and sells financial products.",
    items: [
      "Promote financial inclusion.",
      "Increase transparency.",
      "Encourage responsible financial decisions.",
      "Build trust between consumers and institutions.",
      "Accelerate digital transformation.",
    ],
  },
];

export const CATEGORIES = [
  { name: "Insurance", icon: "shield" },
  { name: "Banking Services", icon: "landmark" },
  { name: "Personal Loans", icon: "wallet" },
  { name: "SME & Business Finance", icon: "briefcase" },
  { name: "Investments & Wealth Management", icon: "trend" },
  { name: "Retirement & Pension Planning", icon: "clock" },
  { name: "Mortgage & Real Estate", icon: "home" },
  { name: "Digital Payments", icon: "radio" },
  { name: "Financial Planning & Advisory", icon: "compass" },
  { name: "Education Financing", icon: "book" },
];

export const ENGINES = [
  {
    id: "education",
    index: "01",
    short: "Education",
    title: "Financial Education Engine",
    kicker: "Knowledge empowers better decisions",
    copy: "Consumers are educated before making financial commitments. Our Education Engine turns complex products into language people can act on.",
    items: [
      "Articles",
      "Financial Guides",
      "Blogs",
      "Podcasts",
      "Videos",
      "Industry Insights",
      "FAQs",
      "Case Studies",
      "Product Explainers",
    ],
    icon: "book",
    art: Images.education,
    artAlt:
      "Illustration of a financial education engine with a graduation cap, open book, calculator and growth charts",
  },
  {
    id: "ai",
    index: "02",
    short: "AI Recommend",
    title: "AI-Assisted Recommendation Engine",
    kicker: "Every consumer’s financial needs are different",
    copy: "Our AI-powered assistant understands customer requirements by asking intelligent questions before recommending suitable financial products and verified providers.",
    items: [
      "Financial objectives",
      "Budget",
      "Product preferences",
      "Risk profile",
      "Eligibility",
      "Personal circumstances",
    ],
    result: "Faster, more accurate and highly personalized product recommendations.",
    icon: "bot",
    art: Images.ai_recommendation,
    artAlt:
      "Illustration of an AI assistant recommending financial products through conversation, sliders and checklists",
  },
  {
    id: "compare",
    index: "03",
    short: "Compare",
    title: "Product Comparison Engine",
    kicker: "Compare financial products side by side with confidence",
    copy: "This transparency helps consumers make informed decisions based on facts rather than marketing.",
    items: [
      "Interest Rates",
      "Premiums",
      "Fees",
      "Benefits",
      "Features",
      "Eligibility Criteria",
      "Coverage",
      "Terms & Conditions",
      "Customer Ratings (future enhancement)",
    ],
    icon: "columns",
    art: Images.compare_illustrator,
    artAlt: "Illustration of two financial products compared side by side under a magnifying glass",
  },
  {
    id: "booking",
    index: "04",
    short: "Booking",
    title: "Appointment Booking",
    kicker: "From discovery to a conversation",
    copy: "Consumers can schedule consultations directly with verified financial institutions, creating a seamless journey from discovery to engagement.",
    items: ["Telephone", "WhatsApp", "Video Meetings", "Physical Appointments"],
    icon: "calendar",
    art: Images.booking,
    artAlt: "Illustration of booking a verified appointment with a financial institution on a calendar",
  },
  {
    id: "leads",
    index: "05",
    short: "Leads",
    title: "Qualified Lead Generation",
    kicker: "Intent-driven, not advertising-driven",
    copy: "Unlike traditional advertising platforms, SimMarket generates leads from consumers who have already learned, compared and expressed interest.",
    items: [
      "Learned",
      "Compared",
      "Asked questions",
      "Expressed interest",
      "Requested appointments",
    ],
    result:
      "Financial institutions receive customers who are already informed and ready to engage, resulting in higher conversion potential.",
    icon: "target",
    art: Images.leads,
    artAlt: "Illustration of a lead funnel turning informed consumers into a qualified lead",
  },
  {
    id: "ccaas",
    index: "06",
    short: "CCaaS",
    title: "Contact Centre as a Service (CCaaS)",
    kicker: "Not every customer converts immediately",
    copy: "Our Contact Centre as a Service re-engages dropped leads, answers questions and returns qualified prospects to service providers.",
    items: [
      "Re-engages dropped leads",
      "Provides additional education",
      "Answers customer questions",
      "Schedules callbacks",
      "Nurtures opportunities",
      "Returns qualified prospects to service providers",
    ],
    result: "This significantly improves lead conversion while enhancing customer experience.",
    icon: "headset",
    art: Images.ccass,
    artAlt:
      "Illustration of a contact-centre agent qualifying prospects and returning them to a financial institution",
  },
  {
    id: "rewards",
    index: "07",
    short: "Rewards",
    title: "Rewards & Loyalty Engine",
    kicker: "Engagement does not stop after registration",
    copy: "These programmes encourage continuous engagement and long-term customer relationships.",
    items: [
      "Reward Points",
      "Cashback",
      "Referral Rewards",
      "Promotional Benefits",
      "Loyalty Incentives",
    ],
    icon: "gift",
    art: Images.rewards,
    artAlt: "Illustration of a rewards and loyalty programme with points, gifts and progress",
  },
];

export const CONSUMER_BENEFITS = [
  {
    title: "Learn before you decide",
    copy: "Access trusted educational content that explains financial products in simple language.",
  },
  {
    title: "Compare with confidence",
    copy: "View side-by-side product comparisons from multiple verified providers.",
  },
  {
    title: "Receive personalized recommendations",
    copy: "AI-powered guidance helps identify products that best match individual needs.",
  },
  {
    title: "Connect with trusted institutions",
    copy: "Book appointments directly with verified financial service providers.",
  },
  {
    title: "Save time",
    copy: "No need to visit multiple websites or branches. Everything is available in one intelligent marketplace.",
  },
];

export const INSTITUTION_BENEFITS = [
  {
    title: "Qualified customer acquisition",
    copy: "Receive highly qualified, intent-driven customer leads.",
  },
  {
    title: "Lower marketing costs",
    copy: "Reduce customer acquisition expenses through targeted engagement.",
  },
  {
    title: "Higher conversion rates",
    copy: "Educated customers convert more effectively than cold prospects.",
  },
  {
    title: "Partner dashboard",
    copy: "Manage products, leads, appointments, reports, campaigns and customer engagement through one secure portal.",
    items: ["Products", "Leads", "Appointments", "Reports", "Campaigns", "Customer Engagement"],
  },
  {
    title: "Business intelligence",
    copy: "Real-time analytics help institutions understand customer behaviour and improve marketing performance.",
  },
];

export const JOURNEY_STEPS = [
  {
    n: "01",
    title: "Discover",
    copy: "Consumers discover SimMarket through digital campaigns, search engines, referrals or social media.",
    icon: "search",
  },
  {
    n: "02",
    title: "Learn",
    copy: "Educational articles, videos, FAQs and financial guides help customers understand available products.",
    icon: "book",
  },
  {
    n: "03",
    title: "Register",
    copy: "Customers create a free account to unlock personalized experiences.",
    icon: "user",
  },
  {
    n: "04",
    title: "Ask questions",
    copy: "Consumers interact with the AI Recommendation Engine to receive tailored guidance.",
    icon: "bot",
  },
  {
    n: "05",
    title: "Compare",
    copy: "Products are compared side-by-side using transparent criteria.",
    icon: "columns",
  },
  {
    n: "06",
    title: "Choose",
    copy: "Customers select the most suitable financial solution.",
    icon: "check",
  },
  {
    n: "07",
    title: "Book",
    copy: "Appointments are scheduled with verified providers.",
    icon: "calendar",
  },
  {
    n: "08",
    title: "Lead generation",
    copy: "Qualified customer information is securely shared with the selected institution.",
    icon: "share",
  },
  {
    n: "09",
    title: "Customer engagement",
    copy: "Institutions engage, convert and serve the customer.",
    icon: "handshake",
  },
  {
    n: "10",
    title: "Rewards",
    copy: "Customers receive continued engagement, education and loyalty benefits.",
    icon: "gift",
  },
];

export const INDUSTRIES = [
  { name: "Commercial Banks", icon: "landmark" },
  { name: "Rural Banks", icon: "building" },
  { name: "Savings & Loans Companies", icon: "wallet" },
  { name: "Credit Unions", icon: "users" },
  { name: "Insurance Companies", icon: "shield" },
  { name: "Investment Firms", icon: "trend" },
  { name: "Pension Providers", icon: "clock" },
  { name: "Mortgage Institutions", icon: "home" },
  { name: "FinTech Companies", icon: "cpu" },
  { name: "Digital Payment Providers", icon: "radio" },
  { name: "SME Finance Organizations", icon: "briefcase" },
  { name: "Financial Advisory Firms", icon: "compass" },
];

export const CAPABILITY_GROUPS = [
  {
    index: "01",
    title: "Marketplace",
    items: [
      "AI Recommendation Engine",
      "Product Comparison Engine",
      "Customer Registration",
      "Appointment Management",
    ],
  },
  {
    index: "02",
    title: "Acquisition",
    items: [
      "Partner Dashboard",
      "Lead Management",
      "Contact Centre Integration",
      "Rewards & Loyalty",
    ],
  },
  {
    index: "03",
    title: "Engagement",
    items: ["SMS Integration", "Email Integration", "WhatsApp Engagement"],
  },
  {
    index: "04",
    title: "Enterprise",
    items: [
      "Reporting & Analytics",
      "Secure API Integration",
      "Role-Based Access Control",
      "Cloud Deployment",
    ],
  },
];

export const DIFFERENTIATORS = [
  "Consumer Education",
  "Artificial Intelligence",
  "Marketplace Technology",
  "Qualified Lead Generation",
  "Contact Centre Services",
  "Customer Nurturing",
  "Appointment Management",
  "Rewards & Loyalty",
  "Partner Dashboards",
  "Business Intelligence",
];

export const VIDEOS = [
  { id: "what", title: "What is SimMarket?", kind: "Overview" },
  { id: "journey", title: "Customer Journey Walkthrough", kind: "Walkthrough" },
  { id: "ai", title: "AI Recommendation Engine Demo", kind: "Product" },
  { id: "compare", title: "Product Comparison Tutorial", kind: "Tutorial" },
  { id: "booking", title: "Appointment Booking Guide", kind: "Tutorial" },
  { id: "dashboard", title: "Partner Dashboard Overview", kind: "Partner" },
  { id: "leads", title: "Lead Management Demonstration", kind: "Partner" },
  { id: "ccaas", title: "Contact Centre & Customer Nurturing", kind: "Operations" },
  { id: "analytics", title: "Reporting & Analytics", kind: "Operations" },
  { id: "stories", title: "Success Stories", kind: "Proof" },
];

export const MARKET_FAQS = FAQ_ITEMS.filter((item) => item.categoryId === "simmarkets");

export const INTEREST_AREAS = [
  "Financial Education",
  "AI Recommendations",
  "Product Comparison",
  "Appointment Booking",
  "Qualified Leads",
  "Contact Centre",
  "Rewards & Loyalty",
  "Partner Dashboard",
];

export const INSTITUTION_TYPES = [
  "Commercial Banks",
  "Rural Banks",
  "Savings & Loans",
  "Credit Unions",
  "Insurance Companies",
  "Investment Companies",
  "Pension Providers",
  "FinTech Companies",
  "Digital Lenders",
  "Mortgage Institutions",
  "Microfinance Institutions",
  "Other",
];

export const APPOINTMENT_TYPES = [
  "Product Demonstration",
  "Partnership Discussion",
  "Technical Consultation",
  "Enterprise Solution Design",
  "Executive Presentation",
];

export const RELATED = [
  {
    id: "simverify",
    name: "SimVerify",
    index: "02",
    copy: "Establish digital trust through borrower, vehicle, device and behavioural verification before approving financial transactions.",
    cta: "Explore SimVerify",
    logo: Images.simverify_logo,
  },
  {
    id: "simcollect",
    name: "SimCollect",
    index: "03",
    copy: "Protect your loan portfolio through intelligent customer engagement, automated repayments and digital debt recovery.",
    cta: "Explore SimCollect",
    logo: Images.simcollect_logo,
  },
];

export const HERO_MEDIA = {
  src: Images.simmarketsHero,
  alt: "West African professionals comparing financial options on a shared digital marketplace",
  spec: "Portrait 4:5 · 1600×2000 · Editorial photograph of West African consumers and advisors around a marketplace screen. Gold #E5B80B and green #34846E lighting. No logos or text in frame. File: /images/simmarkets/hero.png",
};

export const INTRO_MEDIA = {
  src: Images.simmarketsIntro,
  alt: "A consumer learning about financial products before choosing a provider",
  spec: "Landscape 16:10 · 1920×1200 · Candid of an African professional reviewing educational content on a tablet in warm daylight. File: /images/simmarkets/intro.png",
};

export const VIDEO_MEDIA = {
  src: Images.simmarketsVideo,
  alt: "SimMarket product demonstration still",
  spec: "Landscape 16:9 · 1920×1080 · Product demo still of the SimMarket marketplace. No small UI text. File: /images/simmarkets/video.png",
};
